import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Badge } from "@mui/material";
import ButtonComponent from "../button/Button";
import FilterManagerModal from "./FilterManagerModal";
import { getFiltersApi } from "../../../api/filterApi";
import { useMsal } from "@azure/msal-react";

type FilterManagerButtonProps = {
  gridRef: any;
  tableName: string;
  userEmail: string;
  disabled?: boolean;
  appliedFilter?: any;
  externalFilterState?: Record<string, any> | null;
  onApplyExternalFilterState?: (
    state: Record<string, any> | null,
  ) => void;
};

type SavedFilter = {
  default?: boolean;
  activeFlag?: boolean;
  filters?: Record<string, unknown> | null;
};

const FILTER_META_KEY = "__filterManagerMeta";

const splitSavedFilterModel = (model: any) => {
  if (!model || typeof model !== "object") {
    return {
      gridModel: {},
      externalState: null,
    };
  }

  const cloned = { ...model } as Record<string, any>;
  const meta = cloned[FILTER_META_KEY];
  delete cloned[FILTER_META_KEY];

  return {
    gridModel: cloned,
    externalState: meta?.externalState ?? null,
  };
};

const FilterManagerButton: React.FC<FilterManagerButtonProps> = ({
  gridRef,
  tableName,
  userEmail,
  disabled = false,
  appliedFilter,
  externalFilterState,
  onApplyExternalFilterState,
}) => {
  const [open, setOpen] = useState(false);
  const [isApplyingDefault, setIsApplyingDefault] = useState(false);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [hasAppliedSavedFilter, setHasAppliedSavedFilter] = useState(false);
  const explicitApplyRef = useRef(false);
  const mountedRef = useRef(true);
  const lastAppliedKeyRef = useRef("");
  const { accounts } = useMsal();

  const azureUser = accounts && accounts.length > 0 ? accounts[0] : null;
  const azureEmail =
    azureUser?.username || (azureUser?.idTokenClaims as { email?: string } | undefined)?.email || "";
  const effectiveEmail = useMemo(() => azureEmail || userEmail || "", [azureEmail, userEmail]);

  const evaluateAppliedSavedFilter = useCallback((
    nextSavedFilters?: SavedFilter[],
  ) => {
    const api = gridRef?.current?.api;
    if (!api) {
      setHasAppliedSavedFilter(false);
      return;
    }

    const currentGridModel = api.getFilterModel?.() || {};
    const hasGridFilter = Object.keys(currentGridModel).length > 0;

    const list = nextSavedFilters ?? savedFilters;

    const matchesSavedFilter = list.some((savedFilter) => {
      if (savedFilter?.activeFlag === false) return false;

      const { gridModel } =
        splitSavedFilterModel(savedFilter?.filters);

      const isGridMatch =
        JSON.stringify(gridModel || {}) ===
        JSON.stringify(currentGridModel || {});

      return isGridMatch;
    });

    if (matchesSavedFilter) {
      explicitApplyRef.current = true;
      setHasAppliedSavedFilter(true);
      return;
    }

    // Keep badge on after an explicit Apply until the user fully clears filters.
    if (explicitApplyRef.current) {
      const isFullReset = !hasGridFilter;
      if (isFullReset) {
        explicitApplyRef.current = false;
        setHasAppliedSavedFilter(false);
      } else {
        setHasAppliedSavedFilter(true);
      }
      return;
    }

    setHasAppliedSavedFilter(false);
  }, [
    appliedFilter,
    externalFilterState,
    gridRef,
    savedFilters,
  ]);

  const applyDefaultFilter = useCallback(async () => {
    try {
      const api = gridRef?.current?.api;
      if (!api || !tableName || !effectiveEmail) return;

      const applyKey = `${tableName}::${effectiveEmail}`;
      if (lastAppliedKeyRef.current === applyKey) return;
      lastAppliedKeyRef.current = applyKey;

      if (mountedRef.current) {
        setIsApplyingDefault(true);
      }

      const filters = await getFiltersApi(effectiveEmail, tableName);
      const filterList: SavedFilter[] = Array.isArray(filters) ? filters : [];

      if (mountedRef.current) {
        setSavedFilters(filterList);
      }

      const defaultFilter = filterList.find(
        (filter) =>
          Boolean(filter?.default) &&
          filter?.activeFlag !== false,
      );

      if (!defaultFilter?.filters || typeof defaultFilter.filters !== "object") return;

      const { gridModel, externalState } =
        splitSavedFilterModel(
          defaultFilter.filters,
        );

      api.setFilterModel(gridModel);
      onApplyExternalFilterState?.(externalState);

      if (typeof api.onFilterChanged === "function") {
        api.onFilterChanged();
      }

      evaluateAppliedSavedFilter(filterList);
    } catch (error) {
      console.error("Error applying default filter", error);
      lastAppliedKeyRef.current = "";
    } finally {
      if (mountedRef.current) {
        setIsApplyingDefault(false);
      }
    }
  }, [
    effectiveEmail,
    gridRef,
    tableName,
    onApplyExternalFilterState,
  ]);

  /**
   * Auto Apply Default Filter
   */
  useEffect(() => {
    mountedRef.current = true;
    applyDefaultFilter();

    return () => {
      mountedRef.current = false;
    };
  }, [applyDefaultFilter]);

  useEffect(() => {
    const api = gridRef?.current?.api;
    if (!api) {
      setHasAppliedSavedFilter(false);
      return;
    }

    const onFilterChanged = () => {
      evaluateAppliedSavedFilter();
    };

    onFilterChanged();
    api.addEventListener?.("filterChanged", onFilterChanged);

    return () => {
      api.removeEventListener?.("filterChanged", onFilterChanged);
    };
  }, [evaluateAppliedSavedFilter, gridRef]);

  useEffect(() => {
    evaluateAppliedSavedFilter();
  }, [
    appliedFilter,
    externalFilterState,
    evaluateAppliedSavedFilter,
  ]);

  const showFilterBadge = hasAppliedSavedFilter;

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <>
      <Badge
        color="error"
        variant="dot"
        invisible={!showFilterBadge}
        overlap="circular"
      >
        <ButtonComponent
          type="secondary"
          label={isApplyingDefault ? "Loading Filters..." : "Saved Filters"}
          width="auto"
          maxWidth="none"
          disabled={disabled || !effectiveEmail || isApplyingDefault}
          styles={{
            borderRadius: 28,
          }}
          onClick={() => setOpen(true)}
        />
      </Badge>

      <FilterManagerModal
        open={open}
        onClose={handleClose}
        gridRef={gridRef}
        activeFilter={appliedFilter}
        tableName={tableName}
        userEmail={effectiveEmail}
        externalFilterState={externalFilterState}
        onApplyExternalFilterState={
          onApplyExternalFilterState
        }
        onFiltersChanged={(filters) => {
          setSavedFilters(filters);
          evaluateAppliedSavedFilter(filters);
        }}
        onSavedFilterApplied={() => {
          explicitApplyRef.current = true;
          setHasAppliedSavedFilter(true);
        }}
      />
    </>
  );
};

export default FilterManagerButton;